import type {
  ActionCategory,
  ActionCompletion,
  ActionDef,
  ActionDomain,
  ActionSemantics,
} from "./types";

const PRESENTATION_DOMAINS = new Set<ActionDomain>([
  "navigation",
  "external-link",
  "event",
  "ui",
  "motion",
]);

const NON_ATOMIC_ACTIONS = new Set([
  "batchAction",
  "callFlow",
  "conditionalDelay",
  "debounceAction",
  "queueAction",
  "retryAction",
  "throttleAction",
  "transactionAction",
]);

// Actions that synchronously finish before their executor returns. Everything
// else is a completion barrier unless it explicitly has accepted semantics.
// This conservative default keeps async adapters and newly grounded actions
// from being incorrectly published as synchronous.
const IMMEDIATE_ACTIONS = new Set([
  "addTableRow",
  "analyticsEvent",
  "applyReportDesignTheme",
  "compute",
  "computeDateRange",
  "computeField",
  "csvExport",
  "deleteTableRow",
  "formValidate",
  "groupBy",
  "jsonExport",
  "log",
  "mediaControl",
  "notification",
  "printAction",
  "scrollTo",
  "scrollToElement",
  "scrollToPosition",
  "setState",
  "setTableCell",
  "setTheme",
  "setVariable",
  "showLoading",
  "simulateError",
  "sortByField",
  "stopFlow",
  "storage",
  "updateWhere",
  "removeWhere",
  "upsertWhere",
  "validate",
  "websocket",
]);

const ACCEPTED_ACTIONS = new Set([
  "emitEvent",
  "goLink",
  "invokeCallback",
  "navigate",
  "openModal",
  "showPopup",
  "showToast",
]);

const DOMAIN_OVERRIDES: Record<string, ActionDomain> = {
  emitEvent: "event",
  goLink: "external-link",
  invokeCallback: "event",
  navigate: "navigation",
  runMotion: "motion",
};

export type ActionFlowDomainKind =
  | "business"
  | "presentation"
  | "mixed"
  | "empty";

export interface ActionFlowDomainSummary {
  kind: ActionFlowDomainKind;
  domains: ActionDomain[];
}

export function deriveActionSemantics(
  action: Pick<ActionDef, "name" | "category">,
): ActionSemantics {
  return {
    domain: DOMAIN_OVERRIDES[action.name] ?? domainForCategory(action.category),
    atomic: !NON_ATOMIC_ACTIONS.has(action.name),
    completion: completionForAction(action.name),
  };
}

export function classifyActionFlowDomains(
  steps: readonly unknown[],
): ActionFlowDomainSummary {
  const domains = new Set<ActionDomain>();
  visitSteps(steps, domains);
  const ordered = [...domains].sort();
  if (ordered.length === 0) return { kind: "empty", domains: [] };

  const hasPresentation = ordered.some((domain) =>
    PRESENTATION_DOMAINS.has(domain),
  );
  const hasBusiness = ordered.some(
    (domain) => !PRESENTATION_DOMAINS.has(domain),
  );
  return {
    kind:
      hasPresentation && hasBusiness
        ? "mixed"
        : hasPresentation
          ? "presentation"
          : "business",
    domains: ordered,
  };
}

function domainForCategory(category: ActionCategory): ActionDomain {
  if (category === "navigation") return "navigation";
  if (category === "ui") return "ui";
  if (["system", "crypto", "storage", "media"].includes(category))
    return "system";
  return "business";
}

function completionForAction(name: string): ActionCompletion {
  if (ACCEPTED_ACTIONS.has(name)) return "accepted";
  if (IMMEDIATE_ACTIONS.has(name)) return "immediate";
  return "completed";
}

function visitSteps(
  steps: readonly unknown[],
  domains: Set<ActionDomain>,
): void {
  for (const candidate of steps) {
    if (!candidate || typeof candidate !== "object") continue;
    const step = candidate as Record<string, unknown>;
    const actionName =
      typeof step.action === "string"
        ? step.action
        : typeof step.type === "string"
          ? step.type
          : undefined;
    if (actionName) {
      const semantics = ACTION_SEMANTICS_LOOKUP?.[actionName];
      if (semantics) domains.add(semantics.domain);
    }
    for (const field of [
      "then",
      "else",
      "steps",
      "try",
      "catch",
      "finally",
      "default",
      "onEnter",
      "onSuccess",
      "onError",
      "onFinally",
      "compensate",
      "stages",
    ]) {
      if (Array.isArray(step[field]))
        visitSteps(step[field] as unknown[], domains);
    }
    if (
      step.cases &&
      typeof step.cases === "object" &&
      !Array.isArray(step.cases)
    ) {
      for (const branch of Object.values(
        step.cases as Record<string, unknown>,
      )) {
        if (Array.isArray(branch)) visitSteps(branch, domains);
      }
    }
  }
}

// Set by registry.ts after its normalized projection exists. This avoids a
// semantics → registry import cycle while keeping classification source-driven.
let ACTION_SEMANTICS_LOOKUP: Readonly<Record<string, ActionSemantics>> | null =
  null;

export function bindActionSemanticsLookup(
  lookup: Readonly<Record<string, ActionSemantics>>,
): void {
  ACTION_SEMANTICS_LOOKUP = lookup;
}
